import type { BaseQueryFn, EndpointBuilder } from '@reduxjs/toolkit/query'
import { createApi } from '@reduxjs/toolkit/query/react'

import { HTTP_METHODS } from 'common/constants/httpMethodsConstants'
import { baseQuery } from 'common/store/api/fetchBaseQuery'
import { REDUCERS } from 'common/store/reducerConstants'

import {
  transformMedicalIssueResponse,
  transformMedicalIssuesResponse,
} from './medicalIssues.transform'
import type {
  AttachAppointmentIssueBody,
  CreatePatientIssueBody,
  MedicalIssue,
} from './medicalIssues.types'

export enum MEDICAL_ISSUES_TAGS {
  PATIENT_ISSUES = 'PatientMedicalIssues',
  APPOINTMENT_ISSUES = 'AppointmentMedicalIssues',
}

export const medicalIssuesApi = createApi({
  reducerPath: REDUCERS.MEDICAL_ISSUES,
  baseQuery: baseQuery(),
  tagTypes: Object.values(MEDICAL_ISSUES_TAGS),
  endpoints: (build: EndpointBuilder<BaseQueryFn, string, string>) => ({
    fetchPatientMedicalIssues: build.query<MedicalIssue[], { patientId: number }>({
      query: ({ patientId }) => ({
        url: `/emr/patient/${patientId}/medical-issues`,
      }),
      transformResponse: transformMedicalIssuesResponse,
      providesTags: [MEDICAL_ISSUES_TAGS.PATIENT_ISSUES],
    }),

    createPatientMedicalIssue: build.mutation<
      MedicalIssue | null,
      { patientId: number; data: CreatePatientIssueBody }
    >({
      query: ({ patientId, data }) => ({
        url: `/emr/patient/${patientId}/medical-issues`,
        method: HTTP_METHODS.POST,
        data,
      }),
      transformResponse: transformMedicalIssueResponse,
      invalidatesTags: [MEDICAL_ISSUES_TAGS.PATIENT_ISSUES],
    }),

    fetchAppointmentMedicalIssues: build.query<
      MedicalIssue[],
      { appointmentId: number; siteId: string }
    >({
      query: ({ appointmentId, siteId }) => ({
        url: `/emr/appointment/${appointmentId}/medical-issues`,
        headers: { 'Database-Connection': siteId },
      }),
      transformResponse: transformMedicalIssuesResponse,
      providesTags: [MEDICAL_ISSUES_TAGS.APPOINTMENT_ISSUES],
    }),

    attachAppointmentMedicalIssue: build.mutation<
      MedicalIssue | null,
      { appointmentId: number; siteId: string; data: AttachAppointmentIssueBody }
    >({
      query: ({ appointmentId, siteId, data }) => ({
        url: `/emr/appointment/${appointmentId}/medical-issues`,
        method: HTTP_METHODS.POST,
        headers: { 'Database-Connection': siteId },
        data,
      }),
      transformResponse: transformMedicalIssueResponse,
      // A {title} attach also creates a patient problem, so both caches go stale.
      invalidatesTags: [
        MEDICAL_ISSUES_TAGS.APPOINTMENT_ISSUES,
        MEDICAL_ISSUES_TAGS.PATIENT_ISSUES,
      ],
    }),
  }),
})

export const {
  useFetchPatientMedicalIssuesQuery,
  useCreatePatientMedicalIssueMutation,
  useFetchAppointmentMedicalIssuesQuery,
  useAttachAppointmentMedicalIssueMutation,
} = medicalIssuesApi
